// Stage 6: 复盘&转正闭环 - 4个模块
import { useApi, api } from '../api/client';
import { useActions, exportCsv } from '../actions';
import {
  PageHeader, ModuleBanner, Card, CardHead, Kpi, Tag, Loading, ErrorBox, BudgetBar, FunnelRow,
} from '../components/UI';
import { MODULE_META } from '../config';
import { useAI } from '../components/AIPanel';

const STEPS = ['入 职', '第1月', '第2月', '第3月', '复 盘', '转 正'];
const STEP_NUMS = ['01', '02', '03', '04', '05', '06'];

// ===== 6.1 试用期跟踪 =====
export function ProbationTrack() {
  const meta = MODULE_META['probation-track'];
  const { openCreate } = useActions();
  const { open: openAI } = useAI();
  const { data: probs, loading: l1, error: e1 } = useApi(() => api.probations(), [], 'probations');
  const { data: logs, loading: l2, error: e2 } = useApi(() => api.probationLogs(), [], 'probation-logs');
  if (l1 || l2) return <Loading />;
  if (e1 || e2 || !probs || !logs) return <ErrorBox message={e1 || e2 || ''} />;

  return (
    <div className="fade-in">
      <PageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        stageBadge={meta.stageBadge}
        actions={
          <>
            <button className="btn btn-accent" onClick={() => openAI('AI 试用期评估', (m) => api.aiProbationReview({name:'陈思琪',position:'UI设计师',adapt_score:9,work_score:8,culture_score:9,collab_score:8}))}>AI 评估</button>
            <button className="btn btn-primary" onClick={() => openCreate('probation-logs')}>+ 发起月度复盘</button>
          </>
        }
      />
      <ModuleBanner title="试用期员工跟踪" stageTag="Stage 6-1" />

      <div className="grid grid-4" style={{ marginBottom: 24 }}>
        <Kpi label="在试用期" value={4} />
        <Kpi label="本月转正" value={2} trend={{ type: 'up', text: '通过' }} />
        <Kpi label="提前转正" value={1} />
        <Kpi label="不通过" value={0} trend={{ type: 'up', text: '留存率 100%' }} />
      </div>

      <div className="grid grid-2" style={{ gridTemplateColumns: '2fr 1fr', marginBottom: 24 }}>
        <div>
          {probs.map(p => {
            const cur = Math.floor(p.progress / 20);
            return (
              <div key={p.id} className="prob-card">
                <div className="prob-card-head">
                  <div className="prob-name">
                    <div className="avatar-sm" style={{ width: 32, height: 32, background: 'var(--paper-3)', color: 'var(--ink)', fontWeight: 600 }}>
                      {p.name.charAt(0)}
                    </div>
                    <div>
                      <div>{p.name}</div>
                      <div className="text-xs text-ink-3 fw-400">{p.position} · 导师 {p.mentor}</div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="mono fw-600 text-cinnabar">{p.progress}%</div>
                    <div className="text-xs text-ink-3">{p.days}天</div>
                  </div>
                </div>
                <div className="prob-timeline">
                  {STEPS.map((s, i) => (
                    <div key={i} className={`pt-step ${i < cur ? 'done' : i === cur ? 'current' : ''}`}>
                      <div className="pt-n">{STEP_NUMS[i]}</div>
                      <div className="pt-l">{s}</div>
                    </div>
                  ))}
                </div>
                <div className="prob-scores">
                  <div className="ps-item"><div className="ps-num">{p.adapt_score}</div><div className="ps-label">适应</div></div>
                  <div className="ps-item"><div className="ps-num">{p.work_score}</div><div className="ps-label">工作</div></div>
                  <div className="ps-item"><div className="ps-num">{p.culture_score}</div><div className="ps-label">文化</div></div>
                  <div className="ps-item"><div className="ps-num">{p.collab_score}</div><div className="ps-label">协作</div></div>
                </div>
              </div>
            );
          })}
        </div>

        <Card>
          <CardHead title="试用日志" sub={<Tag color="cinnabar">{logs.length}</Tag>} />
          <div className="timeline">
            {logs.map(l => (
              <div key={l.id} className="timeline-item done">
                <div className="timeline-time">{l.date} · {l.type}</div>
                <div className="timeline-desc">{l.desc}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ===== 6.2 转正考核 =====
export function Regularization() {
  const meta = MODULE_META['regularization'];
  const { toast } = useActions();
  return (
    <div className="fade-in">
      <PageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        stageBadge={meta.stageBadge}
        actions={<button className="btn btn-primary" onClick={() => toast('转正流程已发起')}>+ 发起转正</button>}
      />
      <ModuleBanner title="转正考核流程" stageTag="Stage 6-2" />

      <div className="grid grid-4" style={{ marginBottom: 24 }}>
        <Kpi label="待转正" value={2} />
        <Kpi label="本月通过" value={2} trend={{ type: 'up', text: '100%' }} />
        <Kpi label="平均提前" value="3.2" unit="天" />
        <Kpi label="留存率" value={96} unit="%" />
      </div>

      <Card>
        <CardHead title="待转正员工" />
        <table className="table">
          <thead>
            <tr>
              <th>员工</th><th>岗位</th><th>试用起止</th><th>综合评分</th>
              <th>述职材料</th><th>导师评价</th><th>HR 评估</th>
              <th>状态</th><th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><b>陈思琪</b></td><td>UI 设计师</td><td className="text-sm">04-25 ~ 07-25</td>
              <td><b className="mono text-cinnabar">8.92</b></td>
              <td><Tag color="bamboo">已提交</Tag></td>
              <td className="text-sm">9.0 · 优秀</td>
              <td className="text-sm">推荐转正</td>
              <td><span className="status status-progress">待答辩</span></td>
              <td><button className="btn-text" onClick={() => toast('答辩安排中')}>安排</button></td>
            </tr>
            <tr>
              <td><b>李欣怡</b></td><td>产品经理</td><td className="text-sm">04-28 ~ 07-28</td>
              <td><b className="mono text-cinnabar">9.00</b></td>
              <td><Tag color="bamboo">已提交</Tag></td>
              <td className="text-sm">9.2 · 优秀</td>
              <td className="text-sm">推荐转正</td>
              <td><span className="status status-progress">待答辩</span></td>
              <td><button className="btn-text" onClick={() => toast('答辩安排中')}>安排</button></td>
            </tr>
            <tr>
              <td><b>张明远</b></td><td>高级前端</td><td className="text-sm">05-01 ~ 08-01</td>
              <td><b className="mono">—</b></td>
              <td><Tag color="gold">撰写中</Tag></td>
              <td className="text-sm">—</td><td className="text-sm">—</td>
              <td><span className="status status-pending">进行中</span></td>
              <td><button className="btn-text" onClick={() => toast('提醒已发送')}>提醒</button></td>
            </tr>
          </tbody>
        </table>
      </Card>

      <Card>
        <CardHead title="本月已转正 · 2 人" />
        <div className="grid grid-2">
          {[
            { name: '王梓涵', role: '前端开发', date: '07-01', tag: '提前 4 天', comment: '技术扎实，主动承担核心模块，可独当一面。' },
            { name: '何明轩', role: 'Java 后端', date: '07-03', tag: '提前 2 天', comment: '系统设计与性能优化能力强，已承担 P6 工作。' },
          ].map(p => (
            <div key={p.name} style={{ padding: 16, background: 'var(--paper-2)', borderRadius: 'var(--r-md)' }}>
              <div className="flex justify-between items-center">
                <div>
                  <div className="fw-600">{p.name}</div>
                  <div className="text-xs text-ink-3">{p.role} · 转正日期 {p.date}</div>
                </div>
                <Tag color="bamboo">{p.tag}</Tag>
              </div>
              <div className="text-sm text-ink-3" style={{ marginTop: 12 }}>导师评语：{p.comment}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ===== 6.3 招聘数据复盘 =====
export function Analytics() {
  const meta = MODULE_META['analytics'];
  const { toast } = useActions();
  const { open: openAI } = useAI();
  const { data: funnel, loading: l1, error: e1 } = useApi(() => api.funnel(), [], 'funnel');
  const { data: channelAnalysis, loading: l2, error: e2 } = useApi(() => api.channelAnalysis(), [], 'channel-analysis');
  if (l1 || l2) return <Loading />;
  if (e1 || e2 || !funnel || !channelAnalysis) return <ErrorBox message={e1 || e2 || ''} />;

  return (
    <div className="fade-in">
      <PageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        stageBadge={meta.stageBadge}
        actions={
          <>
            <button className="btn btn-accent" onClick={() => openAI('AI 招聘洞察', (m) => api.aiInsights({funnel:funnel,channels:channelAnalysis,period:'2026 Q3'}))}>AI 洞察</button>
            <button className="btn btn-ghost" onClick={() => exportCsv(channelAnalysis as any, '渠道分析.csv')}>导出报告</button>
            <button className="btn btn-primary" onClick={() => toast('已订阅看板')}>订阅看板</button>
          </>
        }
      />
      <ModuleBanner title="核心指标 · 2026 Q3" stageTag="Stage 6-3" />

      <div className="grid grid-5" style={{ marginBottom: 24 }}>
        <Kpi label="到岗周期" value={21} unit="天" trend={{ type: 'up', text: '↓ 5天 较 Q2' }} />
        <Kpi label="简历转化" value="3.9" unit="%" trend={{ type: 'up', text: '↑ 0.6%' }} />
        <Kpi label="渠道成本" value="¥2,840" trend={{ type: 'up', text: '↓ 12%' }} />
        <Kpi label="录用留存" value={96} unit="%" trend={{ type: 'up', text: '↑ 3%' }} />
        <Kpi label="面试流失" value="6.2" unit="%" trend={{ type: 'up', text: '↓ 3%' }} />
      </div>

      <div className="grid grid-2" style={{ marginBottom: 24 }}>
        <Card>
          <CardHead title="招聘漏斗 · 转化率分析" />
          <div className="funnel">
            {funnel.map((f, i) => {
              const color = i < 2 ? 'cinnabar' : i < 4 ? 'bamboo' : i < 6 ? 'gold' : 'ink';
              return <FunnelRow key={f.id} stage={f.stage} count={f.count} pct={f.pct} fillColor={color} />;
            })}
          </div>
        </Card>

        <Card>
          <CardHead title="渠道 ROI 对比" />
          <div className="chart" style={{ height: 220, position: 'relative' }}>
            <svg viewBox="0 0 400 200" style={{ width: '100%', height: '100%' }}>
              {channelAnalysis.map((c, i) => {
                const barWidth = 28;
                const gap = 14;
                const x = i * (barWidth + gap) + 20;
                const maxH = 160;
                const maxCph = 10000;
                const h = (c.cph / maxCph) * maxH;
                const fill = c.cph < 3000 ? 'var(--bamboo)' : c.cph < 6000 ? 'var(--gold)' : 'var(--cinnabar)';
                return (
                  <g key={c.id}>
                    <rect x={x} y={200 - h - 20} width={barWidth} height={h} fill={fill} rx={2} />
                    <text x={x + barWidth / 2} y={195} textAnchor="middle" fontSize={9} fill="var(--ink-3)">
                      {c.name.length > 4 ? c.name.substring(0, 4) : c.name}
                    </text>
                    <text x={x + barWidth / 2} y={200 - h - 25} textAnchor="middle" fontSize={9} fill="var(--ink-2)" fontWeight={600}>
                      {c.cph ? '¥' + c.cph.toLocaleString() : '—'}
                    </text>
                  </g>
                );
              })}
              <line x1={20} y1={180} x2={380} y2={180} stroke="var(--paper-deep)" strokeWidth={1} />
            </svg>
          </div>
          <div className="flex gap-3 justify-center text-xs text-ink-3">
            <span><span style={{ display: 'inline-block', width: 8, height: 8, background: 'var(--bamboo)', marginRight: 4 }} />低 ¥&lt;3K</span>
            <span><span style={{ display: 'inline-block', width: 8, height: 8, background: 'var(--gold)', marginRight: 4 }} />中 ¥3-6K</span>
            <span><span style={{ display: 'inline-block', width: 8, height: 8, background: 'var(--cinnabar)', marginRight: 4 }} />高 ¥&gt;6K</span>
          </div>
        </Card>
      </div>

      <Card>
        <CardHead title="渠道明细分析" sub={`9 个渠道 · 总入职 ${channelAnalysis.reduce((s, c) => s + c.hired, 0)} 人`} />
        <table className="table">
          <thead>
            <tr>
              <th>渠道</th><th>简历</th><th>面试</th><th>Offer</th>
              <th>入职</th><th>转化率</th><th>成本</th>
              <th>单入职成本</th><th>评级</th>
            </tr>
          </thead>
          <tbody>
            {channelAnalysis.map(c => {
              const rate = c.hired > 0 ? ((c.hired / c.resume) * 100).toFixed(1) : '0';
              let grade = { tag: 'tag-outline', text: '—' };
              if (c.cph > 0 && c.cph < 2000) grade = { tag: 'tag-bamboo', text: 'A+' };
              else if (c.cph < 3000) grade = { tag: 'tag-bamboo', text: 'A' };
              else if (c.cph < 5000) grade = { tag: 'tag-gold', text: 'B' };
              else if (c.cph < 8000) grade = { tag: 'tag-gold', text: 'C' };
              else if (c.cph > 0) grade = { tag: 'tag-cinnabar', text: 'D' };

              return (
                <tr key={c.id}>
                  <td><b>{c.name}</b></td>
                  <td className="mono">{c.resume}</td>
                  <td className="mono">{c.interview}</td>
                  <td className="mono">{c.offer}</td>
                  <td className="mono fw-600 text-cinnabar">{c.hired}</td>
                  <td className="mono">{rate}%</td>
                  <td className="mono">¥{c.cost.toLocaleString()}</td>
                  <td className="mono fw-600">{c.cph ? '¥' + c.cph.toLocaleString() : '—'}</td>
                  <td><Tag color={grade.tag as any}>{grade.text}</Tag></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <div className="grid grid-3">
        <Card>
          <CardHead title="到岗周期趋势" />
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div className="kpi-value" style={{ fontSize: 48 }}>21<span className="kpi-unit">天</span></div>
            <div className="kpi-trend down">↓ 5 天 vs Q2</div>
          </div>
          <div className="text-xs text-ink-3" style={{ marginTop: 12 }}>
            {[
              ['需求提报到审批', '2.1 天'],
              ['发布到收简历', '3.5 天'],
              ['简历到 Offer', '11.2 天'],
              ['Offer 到入职', '4.2 天'],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between" style={{ padding: '8px 0', borderBottom: '1px dashed var(--paper-deep)' }}>
                <span>{k}</span><b>{v}</b>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <CardHead title="面试官效率 TOP 5" />
          {[
            { name: 'CTO', count: 12, rate: 67 },
            { name: 'CEO', count: 8, rate: 63 },
            { name: '陈婧 · HR', count: 28, rate: 43 },
            { name: '前端 Leader', count: 22, rate: 41 },
            { name: '设计总监', count: 14, rate: 43 },
          ].map((r, i) => (
            <div key={r.name} className="budget-bar">
              <div className="bb-label">{i + 1}. {r.name}</div>
              <div className="bb-track">
                <div className="bb-fill" style={{ width: `${r.rate * 1.5}%`, background: 'var(--bamboo)' }}>{r.rate}%</div>
              </div>
              <div className="bb-amount text-sm">{r.count}场</div>
            </div>
          ))}
        </Card>

        <Card>
          <CardHead title="部门招聘排行" />
          {[
            { dept: '产品研发部', hired: 8, target: 10, pct: 80 },
            { dept: '算法团队', hired: 3, target: 4, pct: 75 },
            { dept: '设计中心', hired: 2, target: 3, pct: 67 },
            { dept: '数据中台', hired: 1, target: 2, pct: 50 },
            { dept: 'HR 中心', hired: 1, target: 2, pct: 50 },
          ].map(r => (
            <div key={r.dept} className="budget-bar">
              <div className="bb-label">{r.dept}</div>
              <div className="bb-track">
                <div className="bb-fill" style={{ width: `${r.pct}%`, background: 'var(--gold)' }}>{r.hired}/{r.target}</div>
              </div>
              <div className="bb-amount text-sm">{r.pct}%</div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// ===== 6.4 招聘优化 =====
export function Optimize() {
  const meta = MODULE_META['optimize'];
  const { toast } = useActions();
  return (
    <div className="fade-in">
      <PageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        stageBadge={meta.stageBadge}
        actions={<button className="btn btn-primary" onClick={() => toast('优化项功能开发中')}>+ 新增优化项</button>}
      />
      <ModuleBanner title="本季度优化项" stageTag="Stage 6-4" />

      <div className="grid grid-4" style={{ marginBottom: 24 }}>
        <Kpi label="优化项" value={12} />
        <Kpi label="已完成" value={8} trend={{ type: 'up', text: '67%' }} />
        <Kpi label="效果提升" value={23} unit="%" />
        <Kpi label="节省成本" value="¥8.2" unit="万" />
      </div>

      <div className="grid grid-2">
        <Card>
          <CardHead title="渠道优化" />
          {[
            { title: '暂停 V2EX 以外社区渠道', status: '已执行', effect: '聚焦高 ROI 渠道', desc: '社区渠道转化质量参差' },
            { title: 'Boss 直聘简历刷新频率提升', status: '已执行', effect: '简历量 ↑ 18%', desc: '从每日 1 次 → 每 2 小时' },
            { title: '内推奖励上调至 8000 元', status: '执行中', effect: '内推量 ↑ 35%', desc: '关键岗位 P7+ 加码' },
            { title: '校园招聘转向线上专场', status: '已执行', effect: '节省 4.5 万', desc: '减少差旅与场地费用' },
          ].map(o => (
            <div key={o.title} style={{ padding: 14, borderBottom: '1px dashed var(--paper-deep)' }}>
              <div className="flex justify-between items-center">
                <b className="text-sm">{o.title}</b>
                <Tag color={o.status === '已执行' ? 'bamboo' : 'gold'}>{o.status}</Tag>
              </div>
              <div className="text-xs text-ink-3" style={{ marginTop: 8 }}>{o.desc}</div>
              <div className="text-xs text-cinnabar" style={{ marginTop: 8 }}>▸ {o.effect}</div>
            </div>
          ))}
        </Card>

        <Card>
          <CardHead title="流程优化" />
          {[
            { title: 'JD 标准化模板上线', status: '已执行', effect: 'JD 撰写耗时 ↓ 40%', desc: '统一 6 类岗位模板' },
            { title: '面试官培训计划', status: '执行中', effect: '面试通过率 ↑ 8%', desc: '结构化面试 + 评分表' },
            { title: 'AI 简历初筛', status: '试点中', effect: 'HR 用时 ↓ 60%', desc: '智能匹配 + 标签分类' },
            { title: '人才库激活机制', status: '已执行', effect: '激活 38 次 / 成功 6', desc: '月度主动推岗' },
          ].map(o => (
            <div key={o.title} style={{ padding: 14, borderBottom: '1px dashed var(--paper-deep)' }}>
              <div className="flex justify-between items-center">
                <b className="text-sm">{o.title}</b>
                <Tag color={o.status === '已执行' ? 'bamboo' : o.status === '执行中' ? 'gold' : 'outline'}>{o.status}</Tag>
              </div>
              <div className="text-xs text-ink-3" style={{ marginTop: 8 }}>{o.desc}</div>
              <div className="text-xs text-bamboo" style={{ marginTop: 8 }}>▸ {o.effect}</div>
            </div>
          ))}
        </Card>
      </div>

      <Card>
        <CardHead title="Q4 招聘优化路线图" />
        <div className="timeline">
          <div className="timeline-item current">
            <div className="timeline-time">2026 Q3 · 进行中</div>
            <div className="timeline-title">内推激励升级 · 关键岗位专项</div>
            <div className="timeline-desc">P7+ 岗位内推奖励上调至 8000-15000 元，预计内推占比从 18% 提升至 30%</div>
          </div>
          <div className="timeline-item">
            <div className="timeline-time">2026 Q4 · 规划中</div>
            <div className="timeline-title">AI 智能匹配 · 简历精准推荐</div>
            <div className="timeline-desc">基于历史录用数据训练模型，简历与岗位匹配度从 87% 提升至 95%+</div>
          </div>
          <div className="timeline-item">
            <div className="timeline-time">2026 Q4 · 规划中</div>
            <div className="timeline-title">面试官认证体系</div>
            <div className="timeline-desc">建立面试官分级认证，结构化面试评估，提升面试通过率与质量</div>
          </div>
          <div className="timeline-item">
            <div className="timeline-time">2027 Q1 · 规划中</div>
            <div className="timeline-title">雇主品牌升级</div>
            <div className="timeline-desc">技术博客、Open Day、Glassdoor 运营，提升被动候选人转化</div>
          </div>
        </div>
      </Card>
    </div>
  );
}
